import React, { forwardRef } from 'react'
import cx from 'classnames'
import Styles from './Button.module.scss'

import { ButtonLinkProps } from './Types'
import { useLink } from '@react-aria/link'
import { useObjectRef } from 'utils/useObjectRef'
import { useHover } from '@react-aria/interactions'
import { mergeProps } from '@react-aria/utils'
import { useFocusRing } from '@react-aria/focus'
import { AriaLinkProps } from '@react-types/link'

const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>((props, fRef) => {
  const ref = useObjectRef<HTMLAnchorElement>(fRef)
  const { linkProps, isPressed } = useLink(props as AriaLinkProps, ref)
  const { hoverProps, isHovered } = useHover({ ...props, isDisabled: false })
  const { isFocusVisible, focusProps } = useFocusRing()
  const {
    children,
    elementType: Component = 'a',
    size = 'sm',
    variant = 'primary',
    frontIcon: FrontIcon,
    endIcon: EndIcon,
    className,
    iconClassName,
  } = props

  let newLinkProps
  if (Component === 'a') {
    const { to: href, ...rest } = props
    newLinkProps = { ...rest, href }
  }

  const {
    elementType,
    frontIcon,
    endIcon,
    onPress,
    iconClassName: _iconClassName,
    ...finalProps
  } = newLinkProps || props

  const iconOnly = React.Children.count(children) < 1 && (!!FrontIcon || !!EndIcon)

  return (
    <Component
      {...mergeProps(finalProps, linkProps, hoverProps, focusProps)}
      className={cx(Styles.root, className, Styles[size], Styles[variant], Styles.link, {
        [Styles.hovered]: isHovered,
        [Styles.pressed]: isPressed,
        [Styles.focused]: isFocusVisible,
        [Styles.withFrontIcon]: !iconOnly && !!FrontIcon,
        [Styles.withEndIcon]: !iconOnly && !!EndIcon,
      })}
      ref={ref}
    >
      {FrontIcon && <FrontIcon aria-hidden="true" className={cx(Styles.frontIcon, iconClassName)} />}
      {children && <span className={Styles.label}>{children}</span>}
      {EndIcon && <EndIcon aria-hidden="true" className={cx(Styles.endIcon, iconClassName)} />}
    </Component>
  )
})

export { ButtonLink }
